import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Product, ProductService } from '@spartacus/core';
import { of, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import {
  BrandThemesEnum, IComponentWrapper,
  IconSizeEnum,
  SdBannerComponent, SdBorderWrapperComponent,
  SdButtonComponent, SdComponentWrapperComponent,
  SdFormAutocompleteComponent,
  SdFormSelectComponent,
  SdIconComponent,
  SdModalDefaultComponent,
  SdTitleComponent,
  SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import {
  Asset,
  AutocompleteSuggestion,
  CustomBaseStore,
  CustomCategory,
  WC_CONFIG_PROVIDER,
  WcConfig
} from '@seb/web-common/core/models';
import { ProductScopeEnum } from '@seb/web-common/core/enums';
import { LinkMapper, toHeading } from '@seb/web-common/core/mappers';
import {
  BaseStoreService, ConfigurationService,
  CustomAuthService,
  GlobalSearchService, MyAccountService,
  TrackingService
} from '@seb/web-common/core/services';

export interface SelectItem {
  code: string;
  name: string;
}

@Component({
  selector: 'wc-accessory-search',
  templateUrl: './accessory-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SdComponentWrapperComponent,
    SdBorderWrapperComponent,
    SdFormAutocompleteComponent,
    SdIconComponent,
    SdButtonComponent,
    SdTitleComponent,
    SdFormSelectComponent,
    SdModalDefaultComponent,
    SdBannerComponent,
    RouterLink,
    TranslatePipe
  ]
})
export class AccessorySearchComponent implements OnInit, OnDestroy {
  subCategoryItems = signal<SelectItem[]>([]);
  productItems = signal<SelectItem[]>([]);
  accessoryItems = signal<SelectItem[]>([]);

  allCategories: CustomCategory[];
  categoryItems: SelectItem[] = [];
  selectedCategory = '';
  subCategoryControl = new FormControl('');
  selectedSubCategory = '';
  productControl = new FormControl('');
  selectedProduct = '';
  accessoryControl = new FormControl('');
  selectedAccessory = '';

  formGroup: FormGroup;
  isSearchProduct = true;
  isSearchCategoryHidden = true;
  isLogged = false;
  isRefModalOpened = false;
  urlSearchAccessory = '';
  savedUrlSearchCategory = '';
  savedUrlSearchSubCategory = '';
  savedUrlSearchProduct = '';
  urlImage = '/assets/images/asset-accessory-reference.png';
  accessories = [];

  @Output()
  inputChanged = new EventEmitter<string>();

  autocompleteElement = [];
  urlSearchSavedProduct = '';
  isSearchSavedProduct = true;
  savedProductItems: SelectItem[];

  selectedProductName: string;
  selectedAccessoryName: string;
  selectedSavedProductName: string;

  accessorySearchWrapper: IComponentWrapper;
  accessorySearchBackgroundTheme: ThemesEnum | BrandThemesEnum;
  iconSize = IconSizeEnum;

  private readonly destroy$: Subject<void> = new Subject();
  private baseStore: CustomBaseStore;

  @Input() set categories(currentCategories: CustomCategory[]) {
    this.allCategories = currentCategories;
    this.categoryItems = this.toSelectItems(currentCategories);
  }

  constructor(
    private globalSearchService: GlobalSearchService,
    private formBuilder: FormBuilder,
    private baseStoreService: BaseStoreService,
    private authService: CustomAuthService,
    private productService: ProductService,
    private router: Router,
    private myAccountService: MyAccountService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private configService: ConfigurationService,
    private cdRef: ChangeDetectorRef,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig
  ) {
    this.baseStore = this.baseStoreService.baseStore;

    effect(()=> {
      if (this.productItems().length === 0) {
        this.disable(this.productControl);
      } else {
        this.enable(this.productControl);
      }
    });

    effect(()=> {
      if (this.accessoryItems().length === 0) {
        this.disable(this.productControl);
      } else {
        this.enable(this.productControl);
      }
    });

    effect(()=> {
      if (this.subCategoryItems().length === 0) {
        this.disable(this.productControl);
      } else {
        this.enable(this.productControl);
      }
    });
  }

  ngOnInit(): void {
    const baseStore = this.baseStoreService.baseStore;
    const isHob = !!baseStore.houseOfBrandConfig?.brand?.length;
    this.urlImage = isHob ? '/assets/images/hob-asset-accessory-reference.png' : this.urlImage;

    this.accessorySearchBackgroundTheme = this.wcConfig?.accessoryPage?.search?.backgroundTheme;
    this.accessorySearchWrapper = {
      heading: {
        titleLevel: 2,
        size: SizesEnum.l,
        titleThemesConfig: this.wcConfig?.accessoryPage?.search?.titleTheme,
        data: toHeading({
          headline: this.translateService.instant('accessory.search.title')
        })
      },
      footNotes: [],
      links: []
    };

    this.formGroup = this.formBuilder.group({
      autocomplete: []
    });

    this.authService
      .isUserLoggedIn()
      .pipe(
        switchMap((isLogged) => {
          this.isLogged = isLogged;
          return this.isLogged
            ? this.myAccountService.getAssets()
            : of(undefined);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((assets: Asset[]) => {
        const config = this.configService.config;
        this.savedProductItems = assets
          ?.filter(
            (asset) =>
              !!asset &&
              asset.brand === config?.myAccount?.brand &&
              asset.market === this.baseStore?.market &&
              !!asset.cmmf &&
              !!asset.name
          )
          .map((asset) => ({
            code: asset.cmmf,
            name: asset.name
          }));
        this.cdRef.markForCheck();
      });
  }

  setSelectedProduct(event: any): void {
    if (event) {
      this.urlSearchSavedProduct = '/p/' + event.code;
      this.urlSearchSavedProduct = this.convertProductUrlToAccessoryUrl(this.urlSearchSavedProduct);
      this.isSearchSavedProduct = false;
      this.selectedSavedProductName = event.name ? event.name : '';
    } else {
      this.isSearchSavedProduct = true;
    }
  }

  searchProduct(input: string): void {
    this.inputChanged.emit(input);
    if (input.length > 1) {
      this.isSearchProduct = false;
      this.globalSearchService.simpleAutocompleteSearch(input, true).subscribe((result) => {
        this.autocompleteElement = result.results as AutocompleteSuggestion[];
        this.cdRef.markForCheck();
      });
    } else {
      this.isSearchProduct = true;
      this.autocompleteElement = [];
    }
  }

  setCategory(event: SelectItem): void {
    if (event) {
      const isItemChanged = this.selectedCategory !== event.code;
      if (isItemChanged) {
        this.productItems.set([]);
        this.accessoryItems.set([]);
        this.subCategoryControl.reset();
        this.productControl.reset();
        this.accessoryControl.reset();
      }
      const selectedCategory = this.allCategories.find((cat) => cat.code === event.code);

      this.subCategoryItems.set(this.toSelectItems(selectedCategory?.subCategories));
      this.isSearchCategoryHidden = false;

      if (isItemChanged) {
        this.urlSearchAccessory = this.savedUrlSearchCategory = selectedCategory.url;
      }
      this.selectedCategory = event.code;
    } else {
      this.isSearchCategoryHidden = true;
      this.selectedCategory = undefined;
      this.subCategoryItems.set([]);
      this.disable(this.subCategoryControl);
      this.productItems.set([]);
      this.accessoryItems.set([]);
      this.subCategoryControl.reset();
      this.productControl.reset();
      this.accessoryControl.reset();
      this.urlSearchAccessory = '';
    }
  }

  setSubCategory(event: SelectItem): void {
    const isItemChanged = this.selectedSubCategory !== event?.code;
    if (event) {
      if (isItemChanged) {
        this.accessoryItems.set([]);
        this.productControl.reset();
        this.accessoryControl.reset();
      }
      this.baseStoreService.getSisCategories('ACCESSORY', event.code).subscribe((sisCategories) => {
        this.productItems.set(this.toSelectItems(sisCategories?.accessoryCategory?.products));
        this.productItems.update((items) => items?.slice()?.sort((a, b) => a.name.localeCompare(b.name)));
        if (isItemChanged) {
          this.urlSearchAccessory = this.savedUrlSearchSubCategory = sisCategories.accessoryCategory.url;
        }
        this.selectedSubCategory = event.code;
        this.cdRef.markForCheck();
      });
    } else {
      this.selectedSubCategory = undefined;
      this.productItems.set([]);
      this.accessoryItems.set([]);
      this.productControl.reset();
      this.accessoryControl.reset();
      this.urlSearchAccessory = this.savedUrlSearchCategory;
    }
  }

  setProduct(event: SelectItem): void {
    const isItemChanged = this.selectedProduct !== event?.code;

    if (event) {
      if (isItemChanged) {
        this.accessoryControl.reset();
      }
      this.productService
        .get(event.code, ProductScopeEnum.ACCESSORY)
        .pipe(
          filter((p) => !!p),
          takeUntil(this.destroy$)
        )
        .subscribe((product) => {
          this.accessories = product.productReferences?.ACCESSORIES?.map((accessory) => accessory.target);
          this.accessoryItems.set(this.toSelectItems(this.accessories));
          this.accessoryItems.update((items) => items?.slice()?.sort((a, b) => a.name.localeCompare(b.name)));

          if (isItemChanged) {
            this.selectedProductName = product.name ? product.name : '';
            this.convertProductUrlToAccessoryUrl(product.url);
            this.urlSearchAccessory = this.savedUrlSearchProduct = LinkMapper.decodeUrl(
              this.convertProductUrlToAccessoryUrl(product.url)
            );
          }
          this.selectedProduct = event.code;
          this.cdRef.markForCheck();
        });
    } else {
      this.selectedProduct = undefined;
      this.accessoryItems.set([]);
      this.accessoryControl.reset();
      this.urlSearchAccessory = this.savedUrlSearchSubCategory;
      this.selectedProductName = undefined;
    }
  }

  setAccessory(event: SelectItem): void {
    const isItemChanged = this.selectedAccessory !== event?.code;

    if (event && isItemChanged) {
      const currentAccessory = this.accessories.find((accessory) => accessory.code === event.code);
      this.urlSearchAccessory = LinkMapper.decodeUrl(currentAccessory.url);
      this.selectedAccessoryName = currentAccessory.name;
    } else if (!event) {
      this.urlSearchAccessory = this.savedUrlSearchProduct;
      this.selectedAccessoryName = undefined;
    }
    this.selectedAccessory = event?.code;
  }

  login(): void {
    this.authService.toggleLoginWithRedirect('/accessory/home#accessory-search');
  }

  openModalRef(): void {
    this.isRefModalOpened = true;
  }

  onSubmit(): void {
    const query = this.formGroup.get('autocomplete').value;
    this.router.navigateByUrl('/search?query=' + query + '&target=accessoryProduct&isAccessorySearch=true');

    this.trackingService.sendEvent('clickSearchAccessory', {
      request: query
    });
  }

  onAccessorySearchEventClicked(): void {
    const clickSearchAccessoryCharacteristics = {
      characteristics: this.characteristicsBuild()
    };
    this.trackingService.sendEvent('clickSearchAccessoryCharacteristics', clickSearchAccessoryCharacteristics);
  }

  onSavedProductsSearchEventClicked(): void {
    this.trackingService.sendEvent('clickSearchAccessorySavedProducts', {
      productName: this.selectedSavedProductName
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toSelectItems(itemsToMap: CustomCategory[] | Product[]) {
    return (itemsToMap as Array<CustomCategory | Product>)?.map((item: CustomCategory | Product) => ({
      code: item.code,
      name: item.name
    }));
  }

  private convertProductUrlToAccessoryUrl(productUrl: string) {
    const pString = '/p/';
    const lastElevenCharsOfUrl = productUrl?.slice(-11);
    let urlWithAccessory = productUrl?.replace(pString, '/accessory/');
    urlWithAccessory = urlWithAccessory?.replace(lastElevenCharsOfUrl, `/csp${lastElevenCharsOfUrl}`);
    return urlWithAccessory;
  }

  private characteristicsBuild(): string {
    const firstLevelSelected = this.selectedCategory ? this.selectedCategory : '';
    const secondLevelSelected = this.selectedSubCategory ? firstLevelSelected + '/' + this.selectedSubCategory : '';
    const thirdLevelSelected = this.selectedProduct ? secondLevelSelected + '/' + this.selectedProductName : '';
    const forthLevelSelected = this.selectedAccessory ? thirdLevelSelected + '/' + this.selectedAccessoryName : '';

    if (this.selectedAccessory) {
      return forthLevelSelected;
    }
    if (this.selectedProduct) {
      return thirdLevelSelected;
    }
    if (this.selectedSubCategory) {
      return secondLevelSelected;
    }
    if (this.selectedCategory) {
      return firstLevelSelected;
    }
    return '';
  }

  private disable(control): void {
    if (control?.enabled) {
      control.disable();
    }
  }

  private enable(control): void {
    if (control?.disabled) {
      control.enable();
    }
  }
}
